<script>
/**
 * Generic report-print template. Used by accounting reports (Trial Balance,
 * Balance Sheet, P&L, Day Book) and any other tabular report. The host
 * parses query-string filters, hits the API, and passes columns/rows here.
 *
 * Differs from `StoreDocumentPrint` in:
 *   - No party block (reports don't have a buyer/supplier)
 *   - Title sub-line shows the date-range / FY label
 *   - Optional section breaks via `sections` prop (for hierarchical reports
 *     like Balance Sheet — pass a `[{title, columns, rows, totals}]` list)
 *   - Tighter row density (compact mode by default)
 */
export default {
  name: "ReportPrint",
  props: {
    title: { type: String, required: true },
    /** Date-range / FY string shown under the title — e.g. "01 Apr 2026 - 04 May 2026". */
    period: { type: String, default: null },
    voucherDate: { type: [String, Date], default: null },
    /** Single-section flat report mode. */
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    totals: { type: Array, default: () => [] },
    /** Multi-section mode (for hierarchical reports). Each entry:
     *  `{title, columns, rows, totals}`. When supplied, takes precedence
     *  over the flat columns/rows/totals. */
    sections: { type: Array, default: null },
    /** Show the running totals box. */
    showTotals: { type: Boolean, default: true },
    compact: { type: Boolean, default: true },
  },
  computed: {
    sectionList() {
      if (this.sections?.length) return this.sections;
      return [{ title: null, columns: this.columns, rows: this.rows, totals: this.totals }];
    },
  },
};
</script>

<template>
  <PrintLayout
    :voucher-date="voucherDate"
    :title="`${title}${period ? ' — ' + period : ''}`"
  >
    <PrintHeader />
    <PrintTitle :title="title" :subtitle="period" />

    <div v-for="(sec, i) in sectionList" :key="i" class="report-section">
      <h4 v-if="sec.title" class="report-section-title">{{ sec.title }}</h4>
      <PrintTable :columns="sec.columns" :rows="sec.rows" :compact="compact" zebra empty-text="No data">
        <template v-for="col in sec.columns" :key="col.key" #[`cell-${col.key}`]="{ value, row, index }">
          <slot :name="`cell-${col.key}`" :value="value" :row="row" :index="index">
            <template v-if="col.formatter">{{ col.formatter(value, row) }}</template>
            <template v-else>{{ value }}</template>
          </slot>
        </template>
      </PrintTable>
      <PrintTotalsBox v-if="showTotals && sec.totals?.length" :rows="sec.totals" />
    </div>

    <PrintFooter />
  </PrintLayout>
</template>

<style scoped>
.report-section {
  margin-bottom: 4mm;
  page-break-inside: auto;
}

.report-section-title {
  font-size: var(--print-fs-sm);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--print-accent);
  margin: 3mm 0 1mm;
  padding-bottom: 0.5mm;
  border-bottom: 0.25pt solid var(--print-border);
}
</style>
